import { jsPDF } from "jspdf";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import { CircularProgress, Grid, Icon } from "@mui/material";
import pxToRem from "assets/theme/functions/pxToRem";
import { gql, useLazyQuery, useQuery } from "@apollo/client";
import EmpleadoLayout from "components/LayoutContainers/EmpleadoLayout";
import boxShadows from "assets/theme/base/boxShadows";
import PdfHeader from "../../compras/components/pdfHeader";
import { formatMoneyPunto, formatDate } from "utils/formatters";
import dayjs from "dayjs";
import { fromBaseQuantity } from "utils/provisionQuantity";

const QUANTITY_INTEGER_THRESHOLD = 0.09;

const formatDeliveredQuantity = (value) => {
    const numericValue = Number(value) || 0;
    const nearestInteger = Math.round(numericValue);
    const decimalDelta = Math.abs(numericValue - nearestInteger);

    if (decimalDelta <= QUANTITY_INTEGER_THRESHOLD) {
        return nearestInteger;
    }

    return Number(numericValue.toFixed(2)).toLocaleString("es-AR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
    });
};

const hasVisibleAmount = (value) => {
    if (value === null || value === undefined) return false;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue !== 0;
};

const roundMoney = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
const moneyEquals = (left, right) => Math.abs(roundMoney(left) - roundMoney(right)) <= 0.01;

const resolveIvaAmounts = ({ montoIVA105, montoIVA21, alicuotaIVA, montoIVA, montoBase }) => {
    const parsedIva105 = Number(montoIVA105);
    const parsedIva21 = Number(montoIVA21);
    const hasIva105 = Number.isFinite(parsedIva105) && parsedIva105 !== 0;
    const hasIva21 = Number.isFinite(parsedIva21) && parsedIva21 !== 0;

    // Prioriza montos individuales guardados.
    if (hasIva105 || hasIva21) {
        return {
            montoIVA105: hasIva105 ? roundMoney(parsedIva105) : 0,
            montoIVA21: hasIva21 ? roundMoney(parsedIva21) : 0,
            montoIVAGeneral: 0,
        };
    }

    const parsedAli = Number(alicuotaIVA) || 0;
    const base = Number(montoBase) || 0;
    const iva = Number(montoIVA) || 0;

    if (iva > 0 && moneyEquals(parsedAli, 0.315)) {
        const iva105 = roundMoney(iva * (0.105 / 0.315));
        return {
            montoIVA105: iva105,
            montoIVA21: roundMoney(iva - iva105),
            montoIVAGeneral: 0,
        };
    }

    if (iva > 0 && moneyEquals(parsedAli, 0.105)) {
        return {
            montoIVA105: roundMoney(iva),
            montoIVA21: 0,
            montoIVAGeneral: 0,
        };
    }

    if (iva > 0 && moneyEquals(parsedAli, 0.21)) {
        return {
            montoIVA105: 0,
            montoIVA21: roundMoney(iva),
            montoIVAGeneral: 0,
        };
    }

    if (base > 0 && moneyEquals(parsedAli, 0.315)) {
        return {
            montoIVA105: roundMoney(base * 0.105),
            montoIVA21: roundMoney(base * 0.21),
            montoIVAGeneral: 0,
        };
    }

    if (base > 0 && moneyEquals(parsedAli, 0.105)) {
        return {
            montoIVA105: roundMoney(base * 0.105),
            montoIVA21: 0,
            montoIVAGeneral: 0,
        };
    }

    if (base > 0 && moneyEquals(parsedAli, 0.21)) {
        return {
            montoIVA105: 0,
            montoIVA21: roundMoney(base * 0.21),
            montoIVAGeneral: 0,
        };
    }

    // Legacy sin desglose: se muestra como IVA general para no etiquetarlo mal.
    if (iva > 0) {
        return {
            montoIVA105: 0,
            montoIVA21: 0,
            montoIVAGeneral: roundMoney(iva),
        };
    }

    return { montoIVA105: 0, montoIVA21: 0, montoIVAGeneral: 0 };
};

const GET_ORDEN_PAGO = gql`
  query op($id: ID!) {
    pagoProveedor(id: $id) {
      id
      idRemito
      filePathFactura
      estado
      observacion
      pagado
      filePathPagado
      fechaEstimadaPago
      fechaPago
      idUserRechazo
      idUserAceptacion
      formaPago
      cuenta
      numeroFactura
      chequeNro
      monto
      montoBase
      impuestosTotal
      montoTotal
      impuestos {
        alicuotaIVA
        montoBase
        montoIVA
        montoIVA105
        montoIVA21
        montoPercepcionIIBB
        montoPercepcionIVA
        montoOtrosImpuestos
      }
      createdAt
      remito {
        id
        proveedor {
          id
          nombre
          apellido
          razonSocial
          documento
          tipoDocumento
          user {
            email
          }
        }
        provision {
          id
          total
          pedidoCompra {
            id
            numeroExpediente
            tipoCompra
            domicilioEntrega
            fechaEntrega
          }
          detalles {
            id
            materiaPrima {
              nombre
              nombreCompra
              tipoUnidad {
                nombre
                multiplicador
              }
            }
            pedidoCompraDetalle {
              coeficienteCompraConfirmado
              numero
              nombreServicio
              requierePack
              unidadPack
              totalMP
              cantidad
              equivalenciaPack {
                materiaPrima
                packs
              }
            }
            precioUnitario
            cantidad
          }
        }
        movimientoDetalles {
          id
          cantidad
          precio
          materiaPrima {
            nombre
            nombreCompra
            tipoUnidad {
              nombre
              multiplicador
            }
          }
          idProvisionDetalle
        }
      }
    }
  }
`;

const OrdenPagoPDF = () => {
    const documentsRef = useRef(null);
    const { id } = useParams();
    const { lg } = boxShadows;
    const [remito, setRemito] = useState(null);
    const [dataPago, setDataPago] = useState(null);
    const [proveedor, setProveedor] = useState(null);
    const [provision, setProvision] = useState(null);
    const [detalles, setDetalles] = useState([]);

    const { data: dataProvisiones, loading, refetch: refetchProvisiones } = useQuery(GET_ORDEN_PAGO, {
        fetchPolicy: 'cache-and-network',
        variables: {
            id: id,
        },
    });

    useEffect(() => {
        if (dataProvisiones?.pagoProveedor) {
            const { remito, ...data } = dataProvisiones.pagoProveedor;
            setDataPago(data);
            setRemito(remito);
            setProveedor(remito?.proveedor);
            setProvision(remito?.provision);
            setDetalles(remito?.movimientoDetalles || []);
        }
    }, [dataProvisiones]);

    const handleGeneratePdf = () => {
        const pdf = new jsPDF('p', 'pt', 'a4');
        const element = documentsRef.current;

        if (element) {
            pdf.html(element, {
                callback: function (doc) {
                    doc.save(`orden-pago-${dataPago?.id || 'documento'}.pdf`);
                },
                x: 10,
                y: 10,
                width: 575,
                windowWidth: 794
            });
        }
    };

    // Calcular el total del remito usando la misma logica de remitoIndividual
    const totalRemito = detalles.reduce((acc, detalle) => {
        const provisionDetalle = provision?.detalles?.find(
            (pd) => Number(pd.id) === Number(detalle.idProvisionDetalle)
        );

        const pcd = provisionDetalle?.pedidoCompraDetalle;
        const mp = detalle.materiaPrima ?? provisionDetalle?.materiaPrima;

        const cantidadBase = fromBaseQuantity({
            quantityBase: Number(detalle.cantidad) || 0,
            multiplicador: mp?.tipoUnidad?.multiplicador,
            pedidoCompraDetalle: pcd,
        });

        const precioUnit = Number(provisionDetalle?.precioUnitario) || 0;
        const total = cantidadBase * precioUnit;

        return acc + total;
    }, 0);

    const montoBase = Number(dataPago?.montoBase ?? dataPago?.impuestos?.montoBase ?? totalRemito) || 0;
    const montoIVA = Number(dataPago?.impuestos?.montoIVA) || 0;
    const montoPercepcionIIBB = Number(dataPago?.impuestos?.montoPercepcionIIBB) || 0;
    const montoPercepcionIVA = Number(dataPago?.impuestos?.montoPercepcionIVA) || 0;
    const montoOtrosImpuestos = Number(dataPago?.impuestos?.montoOtrosImpuestos) || 0;
    const impuestosTotalCalculado =
        montoIVA + montoPercepcionIIBB + montoPercepcionIVA + montoOtrosImpuestos;
    const impuestosTotal = Number(dataPago?.impuestosTotal ?? impuestosTotalCalculado) || 0;
    const montoTotalOrden = Number(dataPago?.montoTotal ?? dataPago?.monto ?? (montoBase + impuestosTotal)) || 0;
    const ivaAmounts = resolveIvaAmounts({
        montoIVA105: dataPago?.impuestos?.montoIVA105,
        montoIVA21: dataPago?.impuestos?.montoIVA21,
        alicuotaIVA: dataPago?.impuestos?.alicuotaIVA,
        montoIVA,
        montoBase,
    });
    const montoIVA105 = ivaAmounts.montoIVA105;
    const montoIVA21 = ivaAmounts.montoIVA21;
    const montoIVAGeneral = ivaAmounts.montoIVAGeneral;
    const impuestoItemsRaw = [
        {
            label: "Importe Neto Gravado",
            value: dataPago?.montoBase ?? dataPago?.impuestos?.montoBase ?? totalRemito,
        },
        {
            label: "Importe IVA 10,5%",
            value: montoIVA105,
        },
        {
            label: "Importe IVA 21%",
            value: montoIVA21,
        },
        {
            label: "Importe IVA",
            value: montoIVAGeneral,
        },
        {
            label: "Importe Percepcion IIBB",
            value: dataPago?.impuestos?.montoPercepcionIIBB,
        },
        {
            label: "Importe Percepcion IVA",
            value: dataPago?.impuestos?.montoPercepcionIVA,
        },
        {
            label: "Importe Otros tributos",
            value: dataPago?.impuestos?.montoOtrosImpuestos,
        },
        {
            label: "Importe Total impuestos",
            value: dataPago?.impuestosTotal ?? impuestosTotalCalculado,
        },
    ];
    const hasAnyImpuesto = impuestoItemsRaw
        .filter((item) => item.label !== "Importe Neto Gravado")
        .some((item) => hasVisibleAmount(item.value));
    const impuestoItems = (hasAnyImpuesto ? impuestoItemsRaw : [])
        .filter((item) => hasVisibleAmount(item.value))
        .map((item) => ({
            ...item,
            value: Number(item.value),
        }));

    const tipoDocumentos = [
        { id: 80, label: "CUIT" },
        { id: 86, label: "CUIL" },
        { id: 96, label: "DNI" },
    ];

    if (loading || !dataPago) {
        return (
            <EmpleadoLayout>
                <SoftBox
                    p={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="100vh"
                    height="100%"
                >
                    <CircularProgress />
                </SoftBox>
            </EmpleadoLayout>
        );
    }

    return (
        <EmpleadoLayout>
            <SoftBox
                p={2}
                display="flex"
                alignItems="center"
                flexDirection="column"
                minHeight="100vh"
                height="100%"
                gap={4}
            >
                {/* Contenido del PDF */}
                <div ref={documentsRef}>
                    <SoftBox
                        sx={{
                            position: "relative",
                            overflow: "hidden",
                            width: "794px",
                            backgroundColor: "#ffffff",
                            boxShadow: lg,
                            px: 6,
                        }}
                        pb={5}
                    >
                        <PdfHeader />
                        <SoftBox display="flex" flexDirection="column" gap={2} lineHeight={2} fontSize="12px">
                            {/* Encabezado con número de orden y total */}
                            <Grid container border="1pt solid black">
                                <Grid item xs={8} pl={1} fontWeight="bold">
                                    <div>ORDEN DE PAGO N° {dataPago?.id}</div>
                                    <div>REMITO N° {id}</div>
                                </Grid>
                                <Grid item xs={4} textAlign="center" borderLeft="1pt solid black">
                                    <SoftBox borderBottom="1pt solid black" sx={{ backgroundColor: "#f0f0f0" }}>
                                        Monto de esta orden de pago
                                    </SoftBox>
                                    <SoftBox fontWeight="bold">$ {formatMoneyPunto(montoTotalOrden)}</SoftBox>
                                </Grid>
                            </Grid>

                            {/* Datos generales */}
                            <Grid
                                container
                                border="1pt solid black"
                                lineHeight={1.5}
                                sx={{ "& > *:not(:nth-last-child(-n+2))": { borderBottom: "1pt solid black" } }}
                            >
                                <Grid item xs={12} backgroundColor="#f0f0f0" fontWeight="bold" px={1} py={0.5}>
                                    DATOS DEL PAGO
                                </Grid>
                                <Grid
                                    item
                                    xs={3}
                                    borderRight="1pt solid black"
                                    backgroundColor="#f0f0f0"
                                    fontWeight="bold"
                                    px={1}
                                >
                                    N° Factura
                                </Grid>
                                <Grid item xs={9} px={1}>
                                    {dataPago?.numeroFactura ?? "-"}
                                </Grid>
                                <Grid
                                    item
                                    xs={3}
                                    borderRight="1pt solid black"
                                    backgroundColor="#f0f0f0"
                                    fontWeight="bold"
                                    px={1}
                                >
                                    {dataPago?.pagado ? "Fecha de pago" : "Fecha estimada de pago"}
                                </Grid>
                                <Grid item xs={9} px={1}>
                                    {dayjs(dataPago?.fechaPago ?? dataPago?.fechaEstimadaPago).isValid()
                                        ? dayjs(dataPago?.fechaPago ?? dataPago?.fechaEstimadaPago).format("DD/MM/YYYY")
                                        : "-"}
                                </Grid>
                                <Grid
                                    item
                                    xs={3}
                                    borderRight="1pt solid black"
                                    backgroundColor="#f0f0f0"
                                    fontWeight="bold"
                                    px={1}
                                >
                                    Forma de pago
                                </Grid>
                                <Grid item xs={9} px={1}>
                                    {dataPago?.formaPago ?? "-"}
                                </Grid>
                                <Grid
                                    item
                                    xs={3}
                                    borderRight="1pt solid black"
                                    backgroundColor="#f0f0f0"
                                    fontWeight="bold"
                                    px={1}
                                >
                                    {dataPago?.cuenta ? "CBU" : "N° de cheque"}
                                </Grid>
                                <Grid item xs={9} px={1}>
                                    {dataPago?.cuenta ? dataPago?.cuenta : dataPago?.chequeNro}
                                </Grid>
                            </Grid>

                            {/* Proveedor */}
                            <Grid
                                container
                                border="1pt solid black"
                                sx={{ "& > *:not(:nth-last-child(-n+1))": { borderBottom: "1pt solid black" } }}
                            >
                                <Grid item xs={12} backgroundColor="#f0f0f0" fontWeight="bold" px={1} py={0.5}>
                                    DATOS DEL PROVEEDOR
                                </Grid>
                                <Grid
                                    item
                                    xs={6}
                                    borderRight="1pt solid black"
                                    backgroundColor="#f0f0f0"
                                    fontWeight="bold"
                                    px={1}
                                >
                                    {proveedor?.tipoProveedor === "Jurídica" ? "Razón Social" : "Apellido y Nombre"}
                                </Grid>
                                <Grid item xs={6} px={1}>
                                    {proveedor?.razonSocial ?? `${proveedor?.apellido}, ${proveedor?.nombre}`}
                                </Grid>
                                <Grid
                                    item
                                    xs={6}
                                    borderRight="1pt solid black"
                                    backgroundColor="#f0f0f0"
                                    fontWeight="bold"
                                    px={1}
                                >
                                    Tipo de Contratación
                                </Grid>
                                <Grid item xs={6} px={1} borderRight="1pt solid black">
                                    {provision?.pedidoCompra?.tipoCompra?.toUpperCase() ?? "-"}
                                </Grid>
                                <Grid
                                    item
                                    xs={6}
                                    borderRight="1pt solid black"
                                    backgroundColor="#f0f0f0"
                                    fontWeight="bold"
                                    px={1}
                                >
                                    {tipoDocumentos.find((e) => e.id === proveedor?.tipoDocumento)?.label ||
                                        "Documento"}
                                </Grid>
                                <Grid item xs={6} px={1}>
                                    {proveedor?.documento ?? "-"}
                                </Grid>
                                <Grid
                                    item
                                    xs={6}
                                    borderRight="1pt solid black"
                                    backgroundColor="#f0f0f0"
                                    fontWeight="bold"
                                    px={1}
                                >
                                    Email
                                </Grid>
                                <Grid item xs={6} px={1}>
                                    {proveedor?.user?.email ?? "-"}
                                </Grid>
                            </Grid>

                            {/* Detalles */}
                            <Grid
                                container
                                border="1pt solid black"
                                sx={{ "& > *:not(:last-child)": { borderBottom: "1pt solid black" } }}
                            >
                                <Grid item xs={12} backgroundColor="#f0f0f0" fontWeight="bold" px={1} py={0.5}>
                                    DETALLES
                                </Grid>
                                <Grid
                                    item
                                    container
                                    xs={12}
                                    backgroundColor="#f0f0f0"
                                    fontWeight="bold"
                                    sx={{ "& > *:not(:last-child)": { borderRight: "1pt solid black" } }}
                                >
                                    <Grid item xs={0.8} px={1} textAlign="center">Item</Grid>
                                    <Grid item xs={5.2} px={1}>Descripcion</Grid>
                                    <Grid item xs={1.5} px={1} textAlign="center">Cantidad</Grid>
                                    <Grid item xs={1.5} px={1} textAlign="center">Unidad</Grid>
                                    <Grid item xs={1.5} px={1} textAlign="center">Precio U.</Grid>
                                    <Grid item xs={1.5} px={1} textAlign="center">Precio Total</Grid>
                                </Grid>
                                {detalles?.map((detalle, index) => {
                                    const provisionDetalle = provision?.detalles?.find(
                                        (pd) => Number(pd.id) === Number(detalle.idProvisionDetalle)
                                    );
                                    const pcd = provisionDetalle?.pedidoCompraDetalle;
                                    const mp = detalle.materiaPrima ?? provisionDetalle?.materiaPrima;
                                    const cantidadBase = fromBaseQuantity({
                                        quantityBase: Number(detalle.cantidad) || 0,
                                        multiplicador: mp?.tipoUnidad?.multiplicador,
                                        pedidoCompraDetalle: pcd,
                                    });
                                    const precioUnit = Number(provisionDetalle?.precioUnitario) || 0;
                                    const totalDetalle = cantidadBase * precioUnit;

                                    return (
                                        <Grid
                                            key={detalle.id || index}
                                            lineHeight={1.5}
                                            item
                                            container
                                            xs={12}
                                            sx={{ "& > *:not(:last-child)": { borderRight: "1pt solid black" } }}
                                        >
                                            <Grid item xs={0.8} px={1} textAlign="center">
                                                {pcd?.numero || index + 1}
                                            </Grid>
                                            <Grid item xs={5.2} px={1}>
                                                {pcd?.requierePack
                                                    ? mp?.nombreCompra
                                                    : mp?.nombre ?? pcd?.nombreServicio}
                                            </Grid>
                                            <Grid item xs={1.5} px={1} textAlign="center">
                                                {formatDeliveredQuantity(cantidadBase)}
                                            </Grid>
                                            <Grid item xs={1.5} px={1} textAlign="center">
                                                {pcd?.requierePack
                                                    ? pcd?.unidadPack
                                                    : mp?.tipoUnidad?.nombre ?? "-"}
                                            </Grid>
                                            <Grid item xs={1.5} px={1} textAlign="right">
                                                $ {formatMoneyPunto(precioUnit)}
                                            </Grid>
                                            <Grid item xs={1.5} px={1} textAlign="right">
                                                $ {formatMoneyPunto(totalDetalle)}
                                            </Grid>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                            {/* Impuestos */}
                            {impuestoItems.length > 0 && (
                                <Grid
                                    container
                                    border="1pt solid black"
                                    lineHeight={1.5}
                                    sx={{ "& > *:not(:nth-last-child(-n+2))": { borderBottom: "1pt solid black" } }}
                                >
                                    <Grid item xs={12} backgroundColor="#f0f0f0" fontWeight="bold" px={1} py={0.5}>
                                        DETALLE DE IMPUESTOS
                                    </Grid>
                                    {impuestoItems.map((item) => (
                                        <React.Fragment key={item.label}>
                                            <Grid
                                                item
                                                xs={6}
                                                borderRight="1pt solid black"
                                                backgroundColor="#f0f0f0"
                                                fontWeight="bold"
                                                px={1}
                                            >
                                                {item.label}
                                            </Grid>
                                            <Grid item xs={6} px={1} textAlign="right">
                                                $ {formatMoneyPunto(item.value)}
                                            </Grid>
                                        </React.Fragment>
                                    ))}
                                </Grid>
                            )}
                            {/* Total */}
                            <SoftBox border="1px solid black">
                                <SoftBox
                                    px={1}
                                    borderBottom="1px solid black"
                                    bgColor="#f0f0f0"
                                    display="flex"
                                    justifyContent="space-between"
                                    fontWeight="bold"
                                >
                                    <div>IMPORTE TOTAL A PAGAR:</div>
                                    <div>$ {formatMoneyPunto(montoTotalOrden)}</div>
                                </SoftBox>
                                {/* <SoftBox px={1}>{Money2Text(totalRemito)?.toUpperCase()}</SoftBox> */}
                            </SoftBox>

                            {/* Observaciones */}
                            {dataPago?.observacion && (
                                <SoftBox border="1px solid black" px={1} py={1}>
                                    <SoftBox fontWeight="bold" mb={1}>
                                        OBSERVACIONES DEL REMITO:
                                    </SoftBox>
                                    <SoftBox>{dataPago?.observacion}</SoftBox>
                                </SoftBox>
                            )}

                            <SoftBox mt={2}>
                                <Grid container spacing={2} sx={{ mt: 4 }}>
                                    <Grid item xs={6}>
                                        <div
                                            style={{
                                                borderTop: "1px solid black",
                                                textAlign: "center",
                                                paddingTop: "0.5rem",
                                            }}
                                        >
                                            Firma Contable
                                        </div>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <div
                                            style={{
                                                borderTop: "1px solid black",
                                                textAlign: "center",
                                                paddingTop: "0.5rem",
                                            }}
                                        >
                                            Firma Responsable
                                        </div>
                                    </Grid>
                                </Grid>
                            </SoftBox>
                        </SoftBox>
                    </SoftBox>
                </div>

                {/* Botón flotante para descargar PDF */}
                <SoftBox
                    variant="contained"
                    bgColor="white"
                    p={2}
                    style={{
                        position: "fixed",
                        bottom: "1.5rem",
                        right: "1.5rem",
                        zIndex: 1000,
                        boxShadow:
                            "inset 0rem 0rem 0.0625rem 0.0625rem rgb(255 255 255 / 90%), 0rem 1.25rem 1.6875rem 0rem rgb(0 0 0 / 5%)",
                        borderRadius: "16px",
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        backdropFilter: `saturate(200%) blur(${pxToRem(30)})`,
                    }}
                >
                    <Grid container spacing={2} justifyContent="flex-end">
                        <Grid item>
                            <SoftButton variant="gradient" color="error" onClick={handleGeneratePdf}>
                                <Icon fontWeight="bold">picture_as_pdf</Icon>
                                &nbsp;&nbsp;Descargar PDF
                            </SoftButton>
                        </Grid>
                    </Grid>
                </SoftBox>
            </SoftBox>
        </EmpleadoLayout>
    );
};

export default OrdenPagoPDF;

